import { z } from 'zod';

import { createEnv, getOrDefault } from '@libs/core';

const envSchema = z.object({
    ADYEN_MERCHANTID_DIGITAL_CA: z.string().optional(),
    ADYEN_MERCHANTID_DIGITAL_US: z.string().optional(),
    AUTH0_DOMAIN: z.string(),
    AUTH0_M2M_CLIENT_ID_AOS_TOKEN_EXCHANGE: z.string(),
    AUTH0_M2M_CLIENT_ID_AOS: z.string(),
    AUTH0_M2M_CLIENT_SECRET_AOS_TOKEN_EXCHANGE: z.string(),
    AUTH0_M2M_CLIENT_SECRET_AOS: z.string(),
    CART_API_BASE_URL: z.string(),
    COMMERCETOOLS_API_URL: z.string(),
    COMMERCETOOLS_CLIENT_ID: z.string(),
    COMMERCETOOLS_CLIENT_SECRET: z.string(),
    COMMERCETOOLS_PROJECT_KEY: z.string(),
    COMMERCETOOLS_SCOPES: z.string(),
    COMMERCETOOLS_TOKEN_URL: z.string(),
    CONTENTFUL_DELIVERY_ACCESS_TOKEN: z.string(),
    CONTENTFUL_ENVIRONMENT: z.string(),
    CONTENTFUL_PREVIEW_ACCESS_TOKEN: z.string(),
    CONTENTFUL_PREVIEW_MODE: z.string(),
    CONTENTFUL_SPACE_ID: z.string(),
    CWEA_PROXY_URL: z.string().url(),
    EXPERIAN_ADDR_BASE_PATH: z.string().url().optional(),
    EXPERIAN_ADDR_KEY: z.string().optional(),
    EXPERIAN_PHN_BASE_PATH: z.string().optional(),
    EXPERIAN_PHN_KEY: z.string().optional(),
    EXPERIENCE_API_BASE_PATH: z.string(),
    EXPERIENCE_API_HEADER: z.string(),
    EXPERIENCE_API_KEY: z.string(),
    GOOGLE_STATIC_MAPS_SECRET_KEY: z.string(),
    IDENTITY_API_PROXY_URL: z.string().url(),
    IDENTITY_API_SYSTEM_BASE_URL: z.string(),
    IDENTITY_API_WEB_BASE_URL: z.string(),
    IDENTITY_API_WEB_FLOW_VERSION: z.string(),
    IDENTITY_API_WEB_FLOW: z.string(),
    LOYALTY_CONTENTFUL_DELIVERY_ACCESS_TOKEN: z.string(),
    LOYALTY_CONTENTFUL_ENVIRONMENT: z.string(),
    LOYALTY_CONTENTFUL_PREVIEW_ACCESS_TOKEN: z.string(),
    LOYALTY_CONTENTFUL_PREVIEW_MODE: z.string(),
    LOYALTY_CONTENTFUL_SPACE_ID: z.string(),
    NEXT_PUBLIC_ADYEN_CLIENT_KEY: z.string().optional(),
    NEXT_PUBLIC_ADYEN_DATA_COLLECTION_SDK_SCRIPT_URL: z.string(),
    NEXT_PUBLIC_ADYEN_ENVIRONMENT: z.enum(['test', 'live-us', 'dev']).optional(),
    NEXT_PUBLIC_AFTERPAY_CA_MPID: z.string().optional(),
    NEXT_PUBLIC_AFTERPAY_CA_PLACEMENTID_CHECKOUT: z.string().optional(),
    NEXT_PUBLIC_AFTERPAY_CA_PLACEMENTID: z.string().optional(),
    NEXT_PUBLIC_AFTERPAY_SCRIPT_URL: z.string(),
    NEXT_PUBLIC_AFTERPAY_US_MPID: z.string().optional(),
    NEXT_PUBLIC_AFTERPAY_US_PLACEMENTID_CHECKOUT: z.string().optional(),
    NEXT_PUBLIC_AFTERPAY_US_PLACEMENTID: z.string().optional(),
    NEXT_PUBLIC_ALGOLIA_APP_ID: z.string().optional(),
    NEXT_PUBLIC_ALGOLIA_APP_SEARCH_KEY: z.string().optional(),
    NEXT_PUBLIC_ALGOLIA_HOST_URL: z.string().optional(),
    NEXT_PUBLIC_APP_ENV: z.enum(['stage', 'dev', 'qa', 'prod', 'test']),
    NEXT_PUBLIC_APP_NAME: z.enum(['plp', 'pdp', 'cpc', 'hcp', 'acp', 'mtu', 'sa', 'cbu', 'sf']),
    NEXT_PUBLIC_AUTH0_AUDIENCE: z.string(),
    NEXT_PUBLIC_AUTH0_CLIENT_ID_CA: z.string(),
    NEXT_PUBLIC_AUTH0_CLIENT_ID: z.string(),
    NEXT_PUBLIC_AUTH0_DOMAIN: z.string(),
    NEXT_PUBLIC_AUTOSHIP_DISCOUNT_UPDATES_ENABLED: z.stringbool().default(false),
    NEXT_PUBLIC_AUTOSHIP_URL_PATH: z.string().optional(),
    NEXT_PUBLIC_BAZAAR_VOICE_SITE_ID_CA: z.string().optional(),
    NEXT_PUBLIC_BAZAAR_VOICE_SITE_ID_US: z.string(),
    NEXT_PUBLIC_BRANCH_KEY: z.string(),
    NEXT_PUBLIC_BV_ENVIRONMENT: z.enum(['production', 'development', 'staging']),
    NEXT_PUBLIC_COMMIT_SHA: z.string().default('local'),
    NEXT_PUBLIC_COPILOTKIT_DEVTOOLS: z.stringbool().default(false),
    NEXT_PUBLIC_CWEA_PROXY_URL_CA: z.string().url(),
    NEXT_PUBLIC_CWEA_PROXY_URL_US: z.string().url(),
    NEXT_PUBLIC_ENABLE_RECENT_SEARCHES: z.stringbool(),
    NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_ENABLED: z.stringbool().default(false),
    NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_KEY_CA: z.string().optional(),
    NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_KEY_CHECKBOX: z.string().optional(),
    NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_KEY: z.string().optional(),
    NEXT_PUBLIC_EPHEMERAL: z.string().default(''),
    NEXT_PUBLIC_FLIPP_ACCESS_TOKEN: z.string(),
    NEXT_PUBLIC_FLIPP_MERCHANT_ID: z.string(),
    NEXT_PUBLIC_FLIPP_MERCHANT_NAME: z.string(),
    NEXT_PUBLIC_GIFTCARD_RECAPTCHA_ENABLED: z.stringbool().default(false),
    NEXT_PUBLIC_GOOGLE_MAP_API_KEY_CA: z.string(),
    NEXT_PUBLIC_GOOGLE_MAP_API_KEY_US: z.string(),
    NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID: z.string(),
    NEXT_PUBLIC_GTM_CA_AUTH: z.string(),
    NEXT_PUBLIC_GTM_CA_CONTAINER_ID: z.string(),
    NEXT_PUBLIC_GTM_CA_PREVIEW: z.string(),
    NEXT_PUBLIC_GTM_US_AUTH: z.string(),
    NEXT_PUBLIC_GTM_US_CONTAINER_ID: z.string(),
    NEXT_PUBLIC_GTM_US_PREVIEW: z.string(),
    NEXT_PUBLIC_IDENTITY_API_PROXY_URL_CA: z.string().url(),
    NEXT_PUBLIC_IDENTITY_API_PROXY_URL_US: z.string().url(),
    NEXT_PUBLIC_IDENTITY_API_WEB_FLOW_VERSION: z.string(),
    NEXT_PUBLIC_IDENTITY_API_WEB_FLOW: z.string(),
    NEXT_PUBLIC_LEGACYAPP_BASE_URL_CA: z.string().optional(),
    NEXT_PUBLIC_LEGACYAPP_BASE_URL_US: z.string().optional(),
    NEXT_PUBLIC_LOCALE: z.enum(['en-US', 'en-CA']).default('en-US'),
    NEXT_PUBLIC_LOYALTY_ACCOUNT_MENU: z.stringbool(),
    NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_CA: z.string().optional(),
    NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_CANCEL_CA: z.string().optional(),
    NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_CANCEL_US: z.string().optional(),
    NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_CHANGE_DATE_CA: z.string().optional(),
    NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_CHANGE_DATE_US: z.string().optional(),
    NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SEND_NOW_CA: z.string().optional(),
    NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SEND_NOW_US: z.string().optional(),
    NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SKIP_ITEM_CA: z.string().optional(),
    NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SKIP_ITEM_US: z.string().optional(),
    NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SKIP_ORDER_CA: z.string().optional(),
    NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SKIP_ORDER_US: z.string().optional(),
    NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_US: z.string().optional(),
    NEXT_PUBLIC_MEDALLIA_FORM_ID_CA: z.string(),
    NEXT_PUBLIC_MEDALLIA_FORM_ID_US: z.string(),
    NEXT_PUBLIC_MEDALLIA_ID_CA: z.string(),
    NEXT_PUBLIC_MEDALLIA_ID_US: z.string(),
    NEXT_PUBLIC_OG_MERCHANT_ID_CA: z.string(),
    NEXT_PUBLIC_OG_MERCHANT_ID: z.string(),
    NEXT_PUBLIC_OG_STATIC_DOMAIN: z.string().url(),
    NEXT_PUBLIC_ONETRUST_CA_DOMAIN_SCRIPT_ID: z.string(),
    NEXT_PUBLIC_ONETRUST_ENABLED: z.stringbool(),
    NEXT_PUBLIC_ONETRUST_FALLBACK_URL: z.string(),
    NEXT_PUBLIC_ONETRUST_SCRIPT_CA_URL: z.string(),
    NEXT_PUBLIC_ONETRUST_SCRIPT_US_URL: z.string(),
    NEXT_PUBLIC_ONETRUST_US_DOMAIN_SCRIPT_ID: z.string(),
    NEXT_PUBLIC_OPTIMIZELY_PROJECT_ID: z.string(),
    NEXT_PUBLIC_OPTIMIZELY_SDK_KEY: z.string(),
    NEXT_PUBLIC_ORIGIN: z.string(),
    NEXT_PUBLIC_PERFORMANCE_MODE: z.stringbool(),
    NEXT_PUBLIC_RELEASE_VERSION: z.string().default('local'),
    NEXT_PUBLIC_SALSIFY_CLIENT_ID: z.string().optional(),
    NEXT_PUBLIC_SALSIFY_ID_TYPE: z.string().optional(),
    NEXT_PUBLIC_SCENE7_STATIC_BASE: z.string().optional(),
    NEXT_PUBLIC_SENTRY_DSN: z.string().url().optional(),
    NEXT_PUBLIC_SENTRY_ENVIRONMENT: z.enum(['stage', 'dev', 'qa', 'production', 'test']).optional(),
    NEXT_PUBLIC_SITE_PREVIEW: z.stringbool(),
    NEXT_PUBLIC_SLA_COPY_CHANGES_ENABLED: z.stringbool(),
    NEXT_PUBLIC_VET_SOURCE_DIET_ENABLED: z.stringbool().default(false),
    NEXT_SERVER_ACTIONS_ENCRYPTION_KEY: z.string(),
    NODE_ENV: z.enum(['development', 'production', 'test']),
    OCI_APIM_IDENTITY_API_TOKEN: z.string(),
    OG_API_URL: z.string().url(),
    OG_APPLICATION_API_KEY: z.string().optional(),
    OG_MERCHANT_ID_CA: z.string().optional(),
    OG_MERCHANT_ID: z.string().optional(),
    OG_OFFER_ID_CA: z.string().optional(),
    OG_OFFER_ID: z.string().optional(),
    OG_STATIC_DOMAIN: z.string().url().optional(),
    OG_STOREFRONT_API_KEY_CA: z.string().optional(),
    OG_STOREFRONT_API_KEY: z.string().optional(),
    RECAPTCHA_SITE_KEY: z.string().optional(),
    SENTRY_AUTH_TOKEN: z.string(),
    VELOCITY_CA_HOST_DOMAIN: z.string().default('www.petsmart.ca'),
    VELOCITY_PLP_CA_ORIGIN: z.string().optional(),
    VELOCITY_PLP_US_ORIGIN: z.string().optional(),
    VELOCITY_SFCC_CA_ORIGIN: z.string().optional(),
    VELOCITY_SFCC_US_ORIGIN: z.string().optional(),
    VELOCITY_US_HOST_DOMAIN: z.string().default('www.petsmart.com'),
    VERCEL_AUTOMATION_BYPASS_SECRET: z.string().optional(),
    VERCEL_PROTECTION_BYPASS: z.string(),
});

export type EnvSchemaType = z.infer<typeof envSchema>;

export const getENV = getOrDefault(
    createEnv({
        schema: envSchema,

        /**
         * `envs` can be removed after applying to define plugin.
         * For now decided to go this way due to bundle size impact
         */
        envs: {
            ADYEN_MERCHANTID_DIGITAL_CA: process.env.ADYEN_MERCHANTID_DIGITAL_CA,
            ADYEN_MERCHANTID_DIGITAL_US: process.env.ADYEN_MERCHANTID_DIGITAL_US,
            AUTH0_DOMAIN: process.env.AUTH0_DOMAIN,
            AUTH0_M2M_CLIENT_ID_AOS_TOKEN_EXCHANGE: process.env.AUTH0_M2M_CLIENT_ID_AOS_TOKEN_EXCHANGE,
            AUTH0_M2M_CLIENT_ID_AOS: process.env.AUTH0_M2M_CLIENT_ID_AOS,
            AUTH0_M2M_CLIENT_SECRET_AOS: process.env.AUTH0_M2M_CLIENT_SECRET_AOS,
            AUTH0_M2M_CLIENT_SECRET_AOS_TOKEN_EXCHANGE: process.env.AUTH0_M2M_CLIENT_SECRET_AOS_TOKEN_EXCHANGE,
            CART_API_BASE_URL: process.env.CART_API_BASE_URL,
            COMMERCETOOLS_API_URL: process.env.COMMERCETOOLS_API_URL,
            COMMERCETOOLS_CLIENT_ID: process.env.COMMERCETOOLS_CLIENT_ID,
            COMMERCETOOLS_CLIENT_SECRET: process.env.COMMERCETOOLS_CLIENT_SECRET,
            COMMERCETOOLS_PROJECT_KEY: process.env.COMMERCETOOLS_PROJECT_KEY,
            COMMERCETOOLS_SCOPES: process.env.COMMERCETOOLS_SCOPES,
            COMMERCETOOLS_TOKEN_URL: process.env.COMMERCETOOLS_TOKEN_URL,
            CONTENTFUL_DELIVERY_ACCESS_TOKEN: process.env.CONTENTFUL_DELIVERY_ACCESS_TOKEN,
            CONTENTFUL_ENVIRONMENT: process.env.CONTENTFUL_ENVIRONMENT,
            CONTENTFUL_PREVIEW_ACCESS_TOKEN: process.env.CONTENTFUL_PREVIEW_ACCESS_TOKEN,
            CONTENTFUL_PREVIEW_MODE: process.env.CONTENTFUL_PREVIEW_MODE,
            CONTENTFUL_SPACE_ID: process.env.CONTENTFUL_SPACE_ID,
            CWEA_PROXY_URL: process.env.CWEA_PROXY_URL,
            EXPERIAN_ADDR_BASE_PATH: process.env.EXPERIAN_ADDR_BASE_PATH,
            EXPERIAN_ADDR_KEY: process.env.EXPERIAN_ADDR_KEY,
            EXPERIAN_PHN_BASE_PATH: process.env.EXPERIAN_PHN_BASE_PATH,
            EXPERIAN_PHN_KEY: process.env.EXPERIAN_PHN_KEY,
            EXPERIENCE_API_BASE_PATH: process.env.EXPERIENCE_API_BASE_PATH,
            EXPERIENCE_API_HEADER: process.env.EXPERIENCE_API_HEADER,
            EXPERIENCE_API_KEY: process.env.EXPERIENCE_API_KEY,
            GOOGLE_STATIC_MAPS_SECRET_KEY: process.env.GOOGLE_STATIC_MAPS_SECRET_KEY,
            IDENTITY_API_PROXY_URL: process.env.IDENTITY_API_PROXY_URL,
            IDENTITY_API_SYSTEM_BASE_URL: process.env.IDENTITY_API_SYSTEM_BASE_URL,
            IDENTITY_API_WEB_BASE_URL: process.env.IDENTITY_API_WEB_BASE_URL,
            IDENTITY_API_WEB_FLOW_VERSION: process.env.IDENTITY_API_WEB_FLOW_VERSION,
            IDENTITY_API_WEB_FLOW: process.env.IDENTITY_API_WEB_FLOW,
            LOYALTY_CONTENTFUL_DELIVERY_ACCESS_TOKEN: process.env.LOYALTY_CONTENTFUL_DELIVERY_ACCESS_TOKEN,
            LOYALTY_CONTENTFUL_ENVIRONMENT: process.env.LOYALTY_CONTENTFUL_ENVIRONMENT,
            LOYALTY_CONTENTFUL_PREVIEW_ACCESS_TOKEN: process.env.LOYALTY_CONTENTFUL_PREVIEW_ACCESS_TOKEN,
            LOYALTY_CONTENTFUL_PREVIEW_MODE: process.env.LOYALTY_CONTENTFUL_PREVIEW_MODE,
            LOYALTY_CONTENTFUL_SPACE_ID: process.env.LOYALTY_CONTENTFUL_SPACE_ID,
            NEXT_PUBLIC_ADYEN_CLIENT_KEY: process.env.NEXT_PUBLIC_ADYEN_CLIENT_KEY,
            NEXT_PUBLIC_ADYEN_DATA_COLLECTION_SDK_SCRIPT_URL:
                process.env.NEXT_PUBLIC_ADYEN_DATA_COLLECTION_SDK_SCRIPT_URL,
            NEXT_PUBLIC_ADYEN_ENVIRONMENT: process.env.NEXT_PUBLIC_ADYEN_ENVIRONMENT,
            NEXT_PUBLIC_AFTERPAY_CA_MPID: process.env.NEXT_PUBLIC_AFTERPAY_CA_MPID,
            NEXT_PUBLIC_AFTERPAY_CA_PLACEMENTID_CHECKOUT: process.env.NEXT_PUBLIC_AFTERPAY_CA_PLACEMENTID_CHECKOUT,
            NEXT_PUBLIC_AFTERPAY_CA_PLACEMENTID: process.env.NEXT_PUBLIC_AFTERPAY_CA_PLACEMENTID,
            NEXT_PUBLIC_AFTERPAY_SCRIPT_URL: process.env.NEXT_PUBLIC_AFTERPAY_SCRIPT_URL,
            NEXT_PUBLIC_AFTERPAY_US_MPID: process.env.NEXT_PUBLIC_AFTERPAY_US_MPID,
            NEXT_PUBLIC_AFTERPAY_US_PLACEMENTID_CHECKOUT: process.env.NEXT_PUBLIC_AFTERPAY_US_PLACEMENTID_CHECKOUT,
            NEXT_PUBLIC_AFTERPAY_US_PLACEMENTID: process.env.NEXT_PUBLIC_AFTERPAY_US_PLACEMENTID,
            NEXT_PUBLIC_ALGOLIA_APP_ID: process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
            NEXT_PUBLIC_ALGOLIA_APP_SEARCH_KEY: process.env.NEXT_PUBLIC_ALGOLIA_APP_SEARCH_KEY,
            NEXT_PUBLIC_ALGOLIA_HOST_URL: process.env.NEXT_PUBLIC_ALGOLIA_HOST_URL,
            NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
            NEXT_PUBLIC_APP_NAME: process.env.NEXT_PUBLIC_APP_NAME,
            NEXT_PUBLIC_AUTH0_AUDIENCE: process.env.NEXT_PUBLIC_AUTH0_AUDIENCE,
            NEXT_PUBLIC_AUTH0_CLIENT_ID_CA: process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID_CA,
            NEXT_PUBLIC_AUTH0_CLIENT_ID: process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID,
            NEXT_PUBLIC_AUTH0_DOMAIN: process.env.NEXT_PUBLIC_AUTH0_DOMAIN,
            NEXT_PUBLIC_AUTOSHIP_DISCOUNT_UPDATES_ENABLED: process.env.NEXT_PUBLIC_AUTOSHIP_DISCOUNT_UPDATES_ENABLED,
            NEXT_PUBLIC_AUTOSHIP_URL_PATH: process.env.NEXT_PUBLIC_AUTOSHIP_URL_PATH,
            NEXT_PUBLIC_BAZAAR_VOICE_SITE_ID_CA: process.env.NEXT_PUBLIC_BAZAAR_VOICE_SITE_ID_CA,
            NEXT_PUBLIC_BAZAAR_VOICE_SITE_ID_US: process.env.NEXT_PUBLIC_BAZAAR_VOICE_SITE_ID_US,
            NEXT_PUBLIC_BRANCH_KEY: process.env.NEXT_PUBLIC_BRANCH_KEY,
            NEXT_PUBLIC_BV_ENVIRONMENT: process.env.NEXT_PUBLIC_BV_ENVIRONMENT,
            NEXT_PUBLIC_COMMIT_SHA: process.env.NEXT_PUBLIC_COMMIT_SHA,
            NEXT_PUBLIC_COPILOTKIT_DEVTOOLS: process.env.NEXT_PUBLIC_COPILOTKIT_DEVTOOLS,
            NEXT_PUBLIC_CWEA_PROXY_URL_CA: process.env.NEXT_PUBLIC_CWEA_PROXY_URL_CA,
            NEXT_PUBLIC_CWEA_PROXY_URL_US: process.env.NEXT_PUBLIC_CWEA_PROXY_URL_US,
            NEXT_PUBLIC_ENABLE_RECENT_SEARCHES: process.env.NEXT_PUBLIC_ENABLE_RECENT_SEARCHES,
            NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_ENABLED: process.env.NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_ENABLED,
            NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_KEY_CA: process.env.NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_KEY_CA,
            NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_KEY_CHECKBOX: process.env.NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_KEY_CHECKBOX,
            NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_KEY: process.env.NEXT_PUBLIC_ENTERPRISE_RECAPTCHA_KEY,
            NEXT_PUBLIC_EPHEMERAL: process.env.NEXT_PUBLIC_EPHEMERAL,
            NEXT_PUBLIC_FLIPP_ACCESS_TOKEN: process.env.NEXT_PUBLIC_FLIPP_ACCESS_TOKEN,
            NEXT_PUBLIC_FLIPP_MERCHANT_ID: process.env.NEXT_PUBLIC_FLIPP_MERCHANT_ID,
            NEXT_PUBLIC_FLIPP_MERCHANT_NAME: process.env.NEXT_PUBLIC_FLIPP_MERCHANT_NAME,
            NEXT_PUBLIC_GIFTCARD_RECAPTCHA_ENABLED: process.env.NEXT_PUBLIC_GIFTCARD_RECAPTCHA_ENABLED,
            NEXT_PUBLIC_GOOGLE_MAP_API_KEY_CA: process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY_CA,
            NEXT_PUBLIC_GOOGLE_MAP_API_KEY_US: process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY_US,
            NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID: process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID,
            NEXT_PUBLIC_GTM_CA_AUTH: process.env.NEXT_PUBLIC_GTM_CA_AUTH,
            NEXT_PUBLIC_GTM_CA_CONTAINER_ID: process.env.NEXT_PUBLIC_GTM_CA_CONTAINER_ID,
            NEXT_PUBLIC_GTM_CA_PREVIEW: process.env.NEXT_PUBLIC_GTM_CA_PREVIEW,
            NEXT_PUBLIC_GTM_US_AUTH: process.env.NEXT_PUBLIC_GTM_US_AUTH,
            NEXT_PUBLIC_GTM_US_CONTAINER_ID: process.env.NEXT_PUBLIC_GTM_US_CONTAINER_ID,
            NEXT_PUBLIC_GTM_US_PREVIEW: process.env.NEXT_PUBLIC_GTM_US_PREVIEW,
            NEXT_PUBLIC_IDENTITY_API_PROXY_URL_CA: process.env.NEXT_PUBLIC_IDENTITY_API_PROXY_URL_CA,
            NEXT_PUBLIC_IDENTITY_API_PROXY_URL_US: process.env.NEXT_PUBLIC_IDENTITY_API_PROXY_URL_US,
            NEXT_PUBLIC_IDENTITY_API_WEB_FLOW_VERSION: process.env.NEXT_PUBLIC_IDENTITY_API_WEB_FLOW_VERSION,
            NEXT_PUBLIC_IDENTITY_API_WEB_FLOW: process.env.NEXT_PUBLIC_IDENTITY_API_WEB_FLOW,
            NEXT_PUBLIC_LEGACYAPP_BASE_URL_CA: process.env.NEXT_PUBLIC_LEGACYAPP_BASE_URL_CA,
            NEXT_PUBLIC_LEGACYAPP_BASE_URL_US: process.env.NEXT_PUBLIC_LEGACYAPP_BASE_URL_US,
            NEXT_PUBLIC_LOCALE: process.env.NEXT_PUBLIC_LOCALE,
            NEXT_PUBLIC_LOYALTY_ACCOUNT_MENU: process.env.NEXT_PUBLIC_LOYALTY_ACCOUNT_MENU,
            NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_CA: process.env.NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_CA,
            NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_CANCEL_CA:
                process.env.NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_CANCEL_CA,
            NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_CANCEL_US:
                process.env.NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_CANCEL_US,
            NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_CHANGE_DATE_CA:
                process.env.NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_CHANGE_DATE_CA,
            NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_CHANGE_DATE_US:
                process.env.NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_CHANGE_DATE_US,
            NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SEND_NOW_CA:
                process.env.NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SEND_NOW_CA,
            NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SEND_NOW_US:
                process.env.NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SEND_NOW_US,
            NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SKIP_ITEM_CA:
                process.env.NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SKIP_ITEM_CA,
            NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SKIP_ITEM_US:
                process.env.NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SKIP_ITEM_US,
            NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SKIP_ORDER_CA:
                process.env.NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SKIP_ORDER_CA,
            NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SKIP_ORDER_US:
                process.env.NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_SKIP_ORDER_US,
            NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_US: process.env.NEXT_PUBLIC_MEDALLIA_FORM_ID_AUTOSHIP_US,
            NEXT_PUBLIC_MEDALLIA_FORM_ID_CA: process.env.NEXT_PUBLIC_MEDALLIA_FORM_ID_CA,
            NEXT_PUBLIC_MEDALLIA_FORM_ID_US: process.env.NEXT_PUBLIC_MEDALLIA_FORM_ID_US,
            NEXT_PUBLIC_MEDALLIA_ID_CA: process.env.NEXT_PUBLIC_MEDALLIA_ID_CA,
            NEXT_PUBLIC_MEDALLIA_ID_US: process.env.NEXT_PUBLIC_MEDALLIA_ID_US,
            NEXT_PUBLIC_OG_MERCHANT_ID_CA: process.env.NEXT_PUBLIC_OG_MERCHANT_ID_CA,
            NEXT_PUBLIC_OG_MERCHANT_ID: process.env.NEXT_PUBLIC_OG_MERCHANT_ID,
            NEXT_PUBLIC_OG_STATIC_DOMAIN: process.env.NEXT_PUBLIC_OG_STATIC_DOMAIN,
            NEXT_PUBLIC_ONETRUST_CA_DOMAIN_SCRIPT_ID: process.env.NEXT_PUBLIC_ONETRUST_CA_DOMAIN_SCRIPT_ID,
            NEXT_PUBLIC_ONETRUST_ENABLED: process.env.NEXT_PUBLIC_ONETRUST_ENABLED,
            NEXT_PUBLIC_ONETRUST_FALLBACK_URL: process.env.NEXT_PUBLIC_ONETRUST_FALLBACK_URL,
            NEXT_PUBLIC_ONETRUST_SCRIPT_CA_URL: process.env.NEXT_PUBLIC_ONETRUST_SCRIPT_CA_URL,
            NEXT_PUBLIC_ONETRUST_SCRIPT_US_URL: process.env.NEXT_PUBLIC_ONETRUST_SCRIPT_US_URL,
            NEXT_PUBLIC_ONETRUST_US_DOMAIN_SCRIPT_ID: process.env.NEXT_PUBLIC_ONETRUST_US_DOMAIN_SCRIPT_ID,
            NEXT_PUBLIC_OPTIMIZELY_PROJECT_ID: process.env.NEXT_PUBLIC_OPTIMIZELY_PROJECT_ID,
            NEXT_PUBLIC_OPTIMIZELY_SDK_KEY: process.env.NEXT_PUBLIC_OPTIMIZELY_SDK_KEY,
            NEXT_PUBLIC_ORIGIN:
                process.env.NODE_ENV !== 'production' ? 'http://localhost:3000' : process.env.NEXT_PUBLIC_ORIGIN,
            NEXT_PUBLIC_PERFORMANCE_MODE: process.env.NEXT_PUBLIC_PERFORMANCE_MODE,
            NEXT_PUBLIC_RELEASE_VERSION: process.env.NEXT_PUBLIC_RELEASE_VERSION,
            NEXT_PUBLIC_SALSIFY_CLIENT_ID: process.env.NEXT_PUBLIC_SALSIFY_CLIENT_ID,
            NEXT_PUBLIC_SALSIFY_ID_TYPE: process.env.NEXT_PUBLIC_SALSIFY_ID_TYPE,
            NEXT_PUBLIC_SCENE7_STATIC_BASE: process.env.NEXT_PUBLIC_SCENE7_STATIC_BASE,
            NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
            NEXT_PUBLIC_SENTRY_ENVIRONMENT: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
            NEXT_PUBLIC_SITE_PREVIEW: process.env.NEXT_PUBLIC_SITE_PREVIEW,
            NEXT_PUBLIC_SLA_COPY_CHANGES_ENABLED: process.env.NEXT_PUBLIC_SLA_COPY_CHANGES_ENABLED,
            NEXT_PUBLIC_VET_SOURCE_DIET_ENABLED: process.env.NEXT_PUBLIC_VET_SOURCE_DIET_ENABLED,
            NEXT_SERVER_ACTIONS_ENCRYPTION_KEY: process.env.NEXT_SERVER_ACTIONS_ENCRYPTION_KEY,
            NODE_ENV: process.env.NODE_ENV,
            OCI_APIM_IDENTITY_API_TOKEN: process.env.OCI_APIM_IDENTITY_API_TOKEN,
            OG_API_URL: process.env.OG_API_URL,
            OG_APPLICATION_API_KEY: process.env.OG_APPLICATION_API_KEY,
            OG_MERCHANT_ID_CA: process.env.OG_MERCHANT_ID_CA,
            OG_MERCHANT_ID: process.env.OG_MERCHANT_ID,
            OG_OFFER_ID_CA: process.env.OG_OFFER_ID_CA,
            OG_OFFER_ID: process.env.OG_OFFER_ID,
            OG_STATIC_DOMAIN: process.env.OG_STATIC_DOMAIN,
            OG_STOREFRONT_API_KEY_CA: process.env.OG_STOREFRONT_API_KEY_CA,
            OG_STOREFRONT_API_KEY: process.env.OG_STOREFRONT_API_KEY,
            RECAPTCHA_SITE_KEY: process.env.RECAPTCHA_SITE_KEY,
            SENTRY_AUTH_TOKEN: process.env.SENTRY_AUTH_TOKEN,
            VELOCITY_CA_HOST_DOMAIN: process.env.VELOCITY_CA_HOST_DOMAIN,
            VELOCITY_PLP_CA_ORIGIN: process.env.VELOCITY_PLP_CA_ORIGIN,
            VELOCITY_PLP_US_ORIGIN: process.env.VELOCITY_PLP_US_ORIGIN,
            VELOCITY_SFCC_CA_ORIGIN: process.env.VELOCITY_SFCC_CA_ORIGIN,
            VELOCITY_SFCC_US_ORIGIN: process.env.VELOCITY_SFCC_US_ORIGIN,
            VELOCITY_US_HOST_DOMAIN: process.env.VELOCITY_US_HOST_DOMAIN,
            VERCEL_AUTOMATION_BYPASS_SECRET: process.env.VERCEL_AUTOMATION_BYPASS_SECRET,
            VERCEL_PROTECTION_BYPASS: process.env.VERCEL_PROTECTION_BYPASS,
        },
    })
);
